import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { InvitationsService } from "../../shared/services/invitations.service";
import { AuthService } from "../../shared/services/auth.service";
import { ButtonLarge, CustomInput } from "../../shared/uikit";

// Token format patterns
const AUTH_TOKEN_PATTERN = /^[A-Z0-9]{6}$/;
const INVITATION_TOKEN_PATTERN = /^[A-Z0-9]+-[A-Z0-9]{4}$/;

// Combined pattern for URL extraction (supports both 6-char auth and COMPANY-XXXX invitation tokens)
const QR_TOKEN_EXTRACT_PATTERN = /qrToken=([A-Z0-9]+(?:-[A-Z0-9]{4})?)/i;
const AUTH_TOKEN_EXTRACT_PATTERN = /([A-Z0-9]{6})/;
const INVITATION_TOKEN_EXTRACT_PATTERN = /([A-Z0-9]+-[A-Z0-9]{4})/;

type ScanMode = "auth" | "invitation" | null;

/**
 * Extracts a raw token from a QR scan result (string, URL, or object).
 * Returns the token string or empty string if nothing found.
 */
function extractTokenFromScanResult(response: unknown): string {
  let result = "";
  if (typeof response === "string") {
    result = response.trim();
  } else if (response && typeof response === "object") {
    const obj = response as Record<string, unknown>;
    result = String(
      obj.data ?? obj.result ?? obj.value ?? obj.qrToken ?? "",
    ).trim();
  }

  if (!result) return "";

  // If the result is already a bare token (matches either pattern), return as-is
  if (
    AUTH_TOKEN_PATTERN.test(result) ||
    INVITATION_TOKEN_PATTERN.test(result)
  ) {
    return result;
  }

  // Try to extract from URL query parameter qrToken=
  const fromQuery = result.match(QR_TOKEN_EXTRACT_PATTERN);
  if (fromQuery) return fromQuery[1];

  // Try to extract invitation token (COMPANY-XXXX) from path
  const fromInvitationPath = result.match(INVITATION_TOKEN_EXTRACT_PATTERN);
  if (fromInvitationPath) return fromInvitationPath[1];

  // Try to extract auth token (6 chars) from path
  const fromAuthPath = result.match(AUTH_TOKEN_EXTRACT_PATTERN);
  if (fromAuthPath) return fromAuthPath[1];

  return "";
}

export const ScanPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<ScanMode>(null);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const maxBridgeAvailable = !!window.WebApp?.openCodeReader;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Filter: only allow A-Z, 0-9, and hyphen (for invitation tokens)
      const filtered = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
      setToken(filtered);
      setError(null);
    },
    [],
  );

  const scanQrCode = async (scanMode: ScanMode) => {
    setError(null);
    setScanning(true);

    try {
      const webApp = window.WebApp;

      if (!webApp?.openCodeReader) {
        throw new Error("MAX QR scanner is unavailable in this client.");
      }

      // false = camera scanning only (no file selection)
      let response = await webApp.openCodeReader(false);

      // Fallback: если камера не вернула результат (отмена/недоступна),
      // пробуем выбрать файл с изображением QR-кода из галереи устройства.
      if (!response && webApp.openCodeReader) {
        response = await webApp.openCodeReader(true);
      }

      const result = extractTokenFromScanResult(response);

      if (result) {
        setToken(result);
        setError(null);
        await processToken(result, scanMode);
      } else {
        setError("Scan cancelled or no QR code detected.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not scan the QR code.",
      );
    } finally {
      setScanning(false);
    }
  };

  const processToken = async (rawToken: string, scanMode?: ScanMode) => {
    const trimmedToken = rawToken.trim();
    if (!trimmedToken) {
      setError("Please enter a QR token");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const effectiveMode = scanMode ?? mode;

      // If mode is explicitly "invitation", only accept invitation tokens
      if (effectiveMode === "invitation") {
        if (!INVITATION_TOKEN_PATTERN.test(trimmedToken)) {
          setError("Invalid invitation token format (expected COMPANY-XXXX)");
          setLoading(false);
          return;
        }
        await InvitationsService.scanInvitation(trimmedToken);
        navigate(getPath(ConfigRoutes.INVITATION, { qrToken: trimmedToken }));
        return;
      }

      // If mode is explicitly "auth", only accept auth tokens
      if (effectiveMode === "auth") {
        if (!AUTH_TOKEN_PATTERN.test(trimmedToken)) {
          setError("Invalid auth token format (expected 6 characters)");
          setLoading(false);
          return;
        }
        const result = await AuthService.qrScan(trimmedToken);
        navigate(
          getPath(ConfigRoutes.CONFIRM, {
            qrToken: result?.qrToken || trimmedToken,
          }),
        );
        return;
      }

      // No explicit mode — detect token type from format (legacy behavior)
      if (INVITATION_TOKEN_PATTERN.test(trimmedToken)) {
        await InvitationsService.scanInvitation(trimmedToken);
        navigate(getPath(ConfigRoutes.INVITATION, { qrToken: trimmedToken }));
      } else if (AUTH_TOKEN_PATTERN.test(trimmedToken)) {
        const result = await AuthService.qrScan(trimmedToken);
        navigate(
          getPath(ConfigRoutes.CONFIRM, {
            qrToken: result?.qrToken || trimmedToken,
          }),
        );
      } else {
        setError("Invalid token format");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Request failed";
      if (message.includes("expired") || message.includes("404")) {
        setError("Token expired or not found");
      } else if (message.includes("confirmed")) {
        setError("Token already confirmed");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    await processToken(token);
  };

  // --- Chooser screen (no mode selected yet) ---
  if (mode === null) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>📷</div>
        <h2>QR Scanner</h2>
        <p style={{ color: "var(--second-color)" }}>
          Choose what you want to scan:
        </p>

        {/* Scan Device Auth Button */}
        <div style={{ marginTop: "1.5rem" }}>
          <button
            type="button"
            onClick={() => setMode("auth")}
            style={{
              padding: "16px 32px",
              fontSize: "18px",
              fontWeight: 600,
              border: "none",
              borderRadius: "12px",
              background: "var(--accent-color)",
              color: "var(--first-color)",
              cursor: "pointer",
              width: "100%",
              maxWidth: "280px",
            }}
          >
            🖥 Scan Device Login
          </button>
          <p
            style={{
              color: "var(--second-color)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            Authorize a desktop device to your account
          </p>
        </div>

        {/* Scan Invitation Button */}
        <div style={{ marginTop: "1.5rem" }}>
          <button
            type="button"
            onClick={() => setMode("invitation")}
            style={{
              padding: "16px 32px",
              fontSize: "18px",
              fontWeight: 600,
              border: "none",
              borderRadius: "12px",
              background: "var(--color-bg-secondary)",
              color: "var(--first-color)",
              cursor: "pointer",
              width: "100%",
              maxWidth: "280px",
            }}
          >
            📨 Scan Invitation
          </button>
          <p
            style={{
              color: "var(--second-color)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            Accept an invitation to a shared workspace
          </p>
        </div>
      </div>
    );
  }

  // --- Scanner screen (mode selected) ---
  const modeLabel = mode === "auth" ? "Device Login" : "Invitation";
  const modeIcon = mode === "auth" ? "🖥" : "📨";

  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      {/* Back button */}
      <button
        type="button"
        onClick={() => {
          setMode(null);
          setToken("");
          setError(null);
        }}
        style={{
          background: "none",
          border: "none",
          color: "var(--second-color)",
          cursor: "pointer",
          fontSize: "14px",
          padding: "0.5rem",
          marginBottom: "0.5rem",
        }}
      >
        ← Back to scanner selection
      </button>

      <div style={{ fontSize: "48px", marginBottom: "1rem" }}>{modeIcon}</div>
      <h2>Scan {modeLabel}</h2>
      <p style={{ color: "var(--second-color)" }}>
        {mode === "auth"
          ? "Scan the QR code from your desktop device to authorize it."
          : "Scan the invitation QR code to join a shared workspace."}
      </p>

      {/* MAX Bridge Scan Button */}
      {maxBridgeAvailable && (
        <div style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
          <button
            type="button"
            onClick={() => scanQrCode(mode)}
            disabled={scanning || loading}
            style={{
              padding: "14px 32px",
              fontSize: "18px",
              fontWeight: 600,
              border: "none",
              borderRadius: "12px",
              background: "var(--accent-color)",
              color: "var(--first-color)",
              cursor: scanning ? "default" : "pointer",
              width: "100%",
              maxWidth: "280px",
            }}
          >
            {scanning ? "Opening scanner..." : "📷 Scan QR code"}
          </button>
          <p
            style={{
              color: "var(--second-color)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            Uses the native MAX scanner
          </p>
        </div>
      )}

      {/* Manual token input */}
      <div style={{ marginTop: "1.5rem" }}>
        <p
          style={{
            color: "var(--second-color)",
            fontSize: "14px",
            marginBottom: "0.5rem",
          }}
        >
          {maxBridgeAvailable
            ? "Or enter the token manually:"
            : "Enter the token manually:"}
        </p>
        <CustomInput
          placeholder={
            mode === "auth" ? "Enter 6-char token" : "Enter COMPANY-XXXX token"
          }
          value={token}
          onChangeHandler={handleInputChange}
          inputMode="text"
        />

        {/* Error message */}
        {error && (
          <p
            style={{
              color: "var(--color-danger)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            ❌ {error}
          </p>
        )}

        {/* Submit button */}
        {loading ? (
          <div
            style={{
              marginTop: "1rem",
              padding: "12px 24px",
              color: "var(--second-color)",
              fontSize: "16px",
            }}
          >
            Processing...
          </div>
        ) : (
          <ButtonLarge title="Submit" onClick={handleScan} />
        )}
      </div>
    </div>
  );
};
